import React, { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import Logo from "./Logo";
import LanguageToggle from "./LanguageToggle";
import { useT } from "../i18n/LanguageContext";

export default function Navbar() {
  const t = useT();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [pillarsOpen, setPillarsOpen] = useState(false);
  const location = useLocation();

  const PILLAR_LINKS = [
    { key: "body", to: "/body" },
    { key: "mind", to: "/mind" },
    { key: "spirit", to: "/spirit" },
  ];

  const PRIMARY = [
    { key: "method", to: "/method" },
    { key: "curriculum", to: "/curriculum" },
    { key: "about", to: "/about" },
    { key: "resources", to: "/resources" },
    { key: "contact", to: "/contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setPillarsOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#F8F7F2]/90 backdrop-blur-xl border-b border-[rgba(26,26,26,0.06)]"
          : "bg-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between py-5 md:py-6">
        <Link to="/" data-testid="nav-home-logo">
          <Logo size={34} />
        </Link>

        <nav className="hidden lg:flex items-center gap-7 xl:gap-9">
          <NavLink
            to="/method"
            data-testid="nav-link-method"
            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
          >
            {t("nav.method")}
          </NavLink>

          {/* Pillars dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setPillarsOpen(true)}
            onMouseLeave={() => setPillarsOpen(false)}
          >
            <button
              type="button"
              className="nav-link inline-flex items-center gap-1.5"
              onClick={() => setPillarsOpen((v) => !v)}
              data-testid="nav-pillars-toggle"
              aria-expanded={pillarsOpen}
            >
              {t("nav.pillars")}
              <ChevronDown
                size={12}
                className={`transition-transform ${pillarsOpen ? "rotate-180" : ""}`}
              />
            </button>
            {pillarsOpen && (
              <div
                className="absolute top-full left-1/2 -translate-x-1/2 pt-4"
                data-testid="nav-pillars-menu"
              >
                <div className="surface elev-1 min-w-[200px] py-3">
                  {PILLAR_LINKS.map((p) => (
                    <NavLink
                      key={p.key}
                      to={p.to}
                      data-testid={`nav-pillar-${p.key}`}
                      className={({ isActive }) =>
                        `block px-5 py-2.5 text-sm uppercase tracking-[0.22em] transition-colors ${
                          isActive
                            ? "text-[#9A7E47]"
                            : "text-[#4A4A4A] hover:text-[#1A1A1A] hover:bg-[#F3F1EA]"
                        }`
                      }
                    >
                      {t(`nav.${p.key}`)}
                    </NavLink>
                  ))}
                </div>
              </div>
            )}
          </div>

          {PRIMARY.filter((p) => p.key !== "method").map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.key}`}
              className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
              end={item.to === "/"}
            >
              {t(`nav.${item.key}`)}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-5">
          <LanguageToggle />
          <Link to="/curriculum" className="btn-ink" data-testid="nav-cta-button">
            {t("nav.cta")}
          </Link>
        </div>

        <button
          className="lg:hidden text-[#1A1A1A]"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div
          className="lg:hidden bg-[#F8F7F2] border-t border-[rgba(26,26,26,0.06)]"
          data-testid="nav-mobile-panel"
        >
          <div className="container-x py-8 flex flex-col gap-5">
            <NavLink
              to="/method"
              data-testid="nav-mobile-method"
              className={({ isActive }) =>
                `text-2xl font-serif ${isActive ? "text-[#9A7E47]" : "text-[#1A1A1A]"}`
              }
            >
              {t("nav.method")}
            </NavLink>
            <div className="pl-1 text-xs uppercase tracking-[0.32em] text-[#9A7E47] mt-2">
              {t("nav.pillars")}
            </div>
            {PILLAR_LINKS.map((p) => (
              <NavLink
                key={p.key}
                to={p.to}
                className={({ isActive }) =>
                  `text-2xl font-serif pl-4 ${isActive ? "text-[#9A7E47]" : "text-[#2D2D2D]"}`
                }
                data-testid={`nav-mobile-${p.key}`}
              >
                {t(`nav.${p.key}`)}
              </NavLink>
            ))}
            {PRIMARY.filter((x) => x.key !== "method").map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-mobile-${item.key}`}
                className={({ isActive }) =>
                  `text-2xl font-serif ${isActive ? "text-[#9A7E47]" : "text-[#1A1A1A]"}`
                }
              >
                {t(`nav.${item.key}`)}
              </NavLink>
            ))}
            <div className="mt-4">
              <LanguageToggle />
            </div>
            <Link to="/curriculum" className="btn-ink mt-4 w-fit" data-testid="nav-mobile-cta">
              {t("nav.cta")}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
